/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <ListOfProfiles {...cmsData} />)
 * and then make these props fit the final module.
 *
 * Note that propTypes are not needed in this component: the GraphQL schema
 * definition is sufficient for that matter.
 */
import React from "react"
import { graphql } from "gatsby"
import { Section } from "@bw/bits"
import { ListFilters } from "@bw/partials"
import { ListOfProfiles as ListOfProfilesModule } from "@bw/modules"
import useCollectionProfiles from "hooks/useCollectionProfiles"

const ListOfProfiles = ({
  suptitle,
  title,
  subtitle,
  button,
  buttonPosition,
  idAttribute,
  classAttribute,
  profileType,
  backgroundVariant,
  isFollowedByModuleWithBackgroundVariant,
  poi,
  maxItems,
  orderedBy,
  displayFilters,
}) => {
  const profiles = useCollectionProfiles({
    type: profileType?.slug,
    limit: maxItems,
    order: orderedBy?.value,
    poi,
  })

  return (
    <Section
      {...{
        suptitle,
        title,
        subtitle,
        button,
        buttonPosition,
        idAttribute,
        classAttribute,
        backgroundVariant,
        isFollowedByModuleWithBackgroundVariant,
      }}
    >
      {displayFilters ? (
        <ListFilters items={profiles}>
          {({ items }) => <ListOfProfilesModule profiles={items} />}
        </ListFilters>
      ) : (
        <ListOfProfilesModule profiles={profiles} />
      )}
    </Section>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on ListOfProfilesModule {
 *   ...ListOfProfilesModuleFragment
 * }
 *
 * This is also what you will get in the props of your ListOfProfiles
 * resolver component.
 */
export const query = graphql`
  fragment ListOfProfilesModuleFragment on ListOfProfilesModule {
    displayFilters
    maxItems
    poi {
      id
    }
    orderedBy {
      value
    }
    profileType {
      slug
    }
  }
`

export { ListOfProfiles }
